  const isPermissionDeniedError = (error: unknown): boolean => {
    const code = String((error as any)?.code || '').toLowerCase();
    const message = String((error as any)?.message || '').toLowerCase();
    return code.includes('permission-denied') || message.includes('insufficient permissions');
  };
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { Card } from '@/components/ui/card';
import { Chat } from '@/components/messages/chat';
import { OnlineStatusIndicator } from '@/components/user/OnlineStatusIndicator';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFriends } from '@/hooks/useFriends';
import type { FriendRecord } from '@/hooks/useFriends';
import { useMessages } from '@/hooks/useMessages';
import { AIMessagesAdvisorDialog } from '@/components/messages/ai-messages-advisor-dialog';
import { useCall } from '@/components/calls/CallProvider';
import { UniversalMediaUploader } from '@/components/media/UniversalMediaUploader';
import { useMediaUploader, MediaUploaderPresets } from '@/hooks/useMediaUploader';
import { db } from '@/lib/firebase';
import { addDoc, collection, doc, documentId, getDoc, getDocs, limit, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
// import { useLastMessages } from '@/hooks/useLastMessages';
import { 
  Loader2, 
  MessageCircle, 
  Users, 
  Search, 
  Phone, 
  Video, 
  MoreVertical,
  Paperclip,
  MonitorUp,
  Pin,
  Archive,
  Bell,
  Settings,
  Star,
  ChevronLeft,
  ArchiveRestore,
  UsersRound,
  Plus,
  Sparkles,
  AlertTriangle,
  Gauge,
  Clock3,
  TrendingUp,
  TimerReset
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';

interface ConversationPreference {
  pinned: boolean;
  archived: boolean;
}

interface GroupThread {
  id: string;
  name: string;
  participants: string[];
  participantNames?: Record<string, string>;
  createdBy: string;
}

interface GroupMessagePreview {
  id: string;
  groupId: string;
  content: string;
  type?: string;
  senderId: string;
  senderName?: string;
  createdAt: Date;
  seenBy?: Record<string, Date>;
}

interface PriorityConversation {
  id: string;
  kind: 'dm' | 'group';
  label: string;
  unread: number;
  lastAtMs: number;
  score: number;
}

interface ResponseSprintPlan {
  recommendedBatchSize: number;
  expectedUnreadAfterSprint: number;
  estimatedSprintMinutes: number;
  sprintTargets: PriorityConversation[];
}

interface ActiveResponseSprint {
  startedAt: number;
  targetKeys: string[];
  baselineUnreadByKey: Record<string, number>;
}

interface ResponseSprintHistoryItem {
  id: string;
  endedAt: number;
  durationMinutes: number;
  initialUnreadTotal: number;
  reducedUnread: number;
  progressPercent: number;
  completedTargets: number;
  totalTargets: number;
}

interface ConversationUserProfile {
  name?: string;
  displayName?: string;
  photoURL?: string | null;
  username?: string | null;
  isOnline?: boolean;
}

const RESPONSE_SPRINT_HISTORY_KEY = 'messages-response-sprint-history-v1';
const RESPONSE_SPRINT_MODE_KEY = 'messages-response-sprint-mode-v1';

type ResponseSprintMode = 'balanced' | 'focus' | 'rapid';
type SprintRiskLevel = 'low' | 'moderate' | 'high';

export function Messages() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const { startCall, startScreenShareCall, currentCall, isInCall, isScreenSharing, toggleScreenShare } = useCall();
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedUserInfo, setSelectedUserInfo] = useState<FriendRecord['otherUser'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPinned, setShowPinned] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [conversationPreferences, setConversationPreferences] = useState<Record<string, ConversationPreference>>({});
  const [updatingConversationId, setUpdatingConversationId] = useState<string | null>(null);
  const [groupThreads, setGroupThreads] = useState<GroupThread[]>([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [groupLastMessageById, setGroupLastMessageById] = useState<Record<string, GroupMessagePreview>>({});
  const [groupUnreadById, setGroupUnreadById] = useState<Record<string, number>>({});
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedGroupMemberIds, setSelectedGroupMemberIds] = useState<string[]>([]);
  const [retryTick, setRetryTick] = useState(0);
  const [groupStreamsError, setGroupStreamsError] = useState<string | null>(null);
  const [activeResponseSprint, setActiveResponseSprint] = useState<ActiveResponseSprint | null>(null);
  const [responseSprintHistory, setResponseSprintHistory] = useState<ResponseSprintHistoryItem[]>([]);
  const [responseSprintMode, setResponseSprintMode] = useState<ResponseSprintMode>('balanced');
  const [isEphemeralModeActive, setIsEphemeralModeActive] = useState(false);
  const [extraConversationUsers, setExtraConversationUsers] = useState<Record<string, ConversationUserProfile>>({});
  const navigate = useNavigate();

    useEffect(() => {
      setIsEphemeralModeActive(false);
    }, [selectedUser, selectedGroupId]);
  const [isAICopilotOpen, setIsAICopilotOpen] = useState(false);
  const [aiChatTitle, setAiChatTitle] = useState('');
  const [aiInitialSnippet, setAiInitialSnippet] = useState('');
  const location = useLocation();
  const { friends, loading: friendsLoading, error } = useFriends(retryTick);
  const {
    messages,
    lastMessageByUser,
    unreadByUser,
    loading: messagesLoading,
    error: messagesError,
  } = useMessages(retryTick);

  useEffect(() => {
    if (!user?.uid) {
      setExtraConversationUsers({});
      return;
    }

    const friendIds = new Set(friends.map((friend) => friend.otherUser.id));
    const missingUserIds = Object.keys(lastMessageByUser).filter(
      (id) => id && id !== user.uid && !friendIds.has(id)
    );

    if (missingUserIds.length === 0) {
      setExtraConversationUsers({});
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const nextProfiles: Record<string, ConversationUserProfile> = {};
        const uniqueIds = Array.from(new Set(missingUserIds));

        for (let i = 0; i < uniqueIds.length; i += 10) {
          const chunk = uniqueIds.slice(i, i + 10);
          const usersSnap = await getDocs(
            query(collection(db, 'users'), where(documentId(), 'in', chunk))
          );

          usersSnap.forEach((snapshotDoc) => {
            nextProfiles[snapshotDoc.id] = snapshotDoc.data() as ConversationUserProfile;
          });
        }

        if (!cancelled) {
          setExtraConversationUsers(nextProfiles);
        }
      } catch (fetchError) {
        if (!cancelled) {
          console.error('Failed to load extra conversation users:', fetchError);
          setExtraConversationUsers({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [friends, lastMessageByUser, user?.uid]);

  const directConversations = useMemo(() => {
    const friendIds = new Set(friends.map((friend) => friend.otherUser.id));
    const missingUserIds = Object.keys(lastMessageByUser).filter(
      (id) => id && id !== user?.uid && !friendIds.has(id)
    );

    const fallbackConversations: FriendRecord[] = missingUserIds.map((id) => {
      const profile = extraConversationUsers[id];
      const displayName =
        (typeof profile?.name === 'string' && profile.name.trim()) ||
        (typeof profile?.displayName === 'string' && profile.displayName.trim()) ||
        'User';

      return {
        id: `message-${id}`,
        status: 'accepted',
        participants: user?.uid ? [user.uid, id] : [id],
        otherUser: {
          id,
          name: displayName,
          photoURL: profile?.photoURL || null,
          username: profile?.username || null,
          isOnline: Boolean(profile?.isOnline),
        },
      } as FriendRecord;
    });

    return [...friends, ...fallbackConversations];
  }, [extraConversationUsers, friends, lastMessageByUser, user?.uid]);
  
  // Media uploader for message attachments
  const messageMediaUploader = useMediaUploader({
    config: MediaUploaderPresets.message,
    onUpload: async (files) => {
      console.log('Uploading message attachments:', files);
      // Handle message attachment upload
    }
  });

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    // Set loading to false once friends and messages are loaded
    if (!friendsLoading && !messagesLoading) {
      setLoading(false);
    }
  }, [user, navigate, friendsLoading, messagesLoading]);

  useEffect(() => {
    if (typeof window === 'undefined' || !user?.uid) {
      setResponseSprintHistory([]);
      return;
    }

    try {
      const parsed = JSON.parse(window.localStorage.getItem(RESPONSE_SPRINT_HISTORY_KEY) || '{}') as Record<string, ResponseSprintHistoryItem[]>;
      const userHistory = Array.isArray(parsed[user.uid]) ? parsed[user.uid] : [];
      setResponseSprintHistory(userHistory.slice(0, 30));
    } catch {
      setResponseSprintHistory([]);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (typeof window === 'undefined' || !user?.uid) {
      return;
    }

    try {
      const parsed = JSON.parse(window.localStorage.getItem(RESPONSE_SPRINT_HISTORY_KEY) || '{}') as Record<string, ResponseSprintHistoryItem[]>;
      parsed[user.uid] = responseSprintHistory.slice(0, 30);
      window.localStorage.setItem(RESPONSE_SPRINT_HISTORY_KEY, JSON.stringify(parsed));
    } catch {
      // Ignore storage write failures in private modes.
    }
  }, [responseSprintHistory, user?.uid]);

  useEffect(() => {
    if (typeof window === 'undefined' || !user?.uid) {
      setResponseSprintMode('balanced');
      return;
    }

    try {
      const parsed = JSON.parse(window.localStorage.getItem(RESPONSE_SPRINT_MODE_KEY) || '{}') as Record<string, ResponseSprintMode>;
      const storedMode = parsed[user.uid];
      if (storedMode === 'focus' || storedMode === 'rapid' || storedMode === 'balanced') {
        setResponseSprintMode(storedMode);
      } else {
        setResponseSprintMode('balanced');
      }
    } catch {
      setResponseSprintMode('balanced');
    }
  }, [user?.uid]);

  useEffect(() => {
    if (typeof window === 'undefined' || !user?.uid) {
      return;
    }

    try {
      const parsed = JSON.parse(window.localStorage.getItem(RESPONSE_SPRINT_MODE_KEY) || '{}') as Record<string, ResponseSprintMode>;
      parsed[user.uid] = responseSprintMode;
      window.localStorage.setItem(RESPONSE_SPRINT_MODE_KEY, JSON.stringify(parsed));
    } catch {
      // Ignore storage write failures.
    }
  }, [responseSprintMode, user?.uid]);

  useEffect(() => {
    if (!user) {
      setGroupThreads([]);
      setGroupsLoaded(false);
      setGroupLastMessageById({});
      setGroupUnreadById({});
      setGroupStreamsError(null);
      return;
    }

    setGroupStreamsError(null);
    setGroupsLoaded(false);

    const groupsQuery = query(
      collection(db, 'messageGroups'),
      where('participants', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(
      groupsQuery,
      (snapshot) => {
        const nextGroups: GroupThread[] = snapshot.docs.map((snapshotDoc) => {
          const data = snapshotDoc.data() as Partial<GroupThread> & {
            participantNames?: Record<string, string>;
          };

          return {
            id: snapshotDoc.id,
            name: typeof data.name === 'string' && data.name.trim() ? data.name.trim() : 'Group Chat',
            participants: Array.isArray(data.participants) ? data.participants : [],
            participantNames: data.participantNames,
            createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
          };
        });

        setGroupThreads(nextGroups);
        setGroupsLoaded(true);
        setGroupStreamsError(null);
      },
      (error) => {
        if (!isPermissionDeniedError(error)) {
          console.error('Failed to listen to message groups:', error);
        }
        setGroupsLoaded(true);
        setGroupStreamsError(
          isPermissionDeniedError(error)
            ? 'Group chats are not available for this account.'
            : 'Failed to sync group chats.'
        );
      }
    );

    return () => unsubscribe();
  }, [user?.uid, retryTick]);

  useEffect(() => {
    if (!user) return;

    setGroupStreamsError(null);

    const messagesQuery = query(
      collection(db, 'messages'),
      where('participants', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        const groupMessages = snapshot.docs
          .map((snapshotDoc) => {
            const data = snapshotDoc.data() as {
              groupId?: string;
              content?: string;
              type?: string;
              senderId?: string;
              senderName?: string;
              createdAt?: { toDate?: () => Date } | string | Date;
              seenBy?: Record<string, string | Date | { toDate?: () => Date }>;
            };

            if (!data.groupId) return null;

            const createdAt =
              data.createdAt && typeof (data.createdAt as { toDate?: () => Date }).toDate === 'function'
                ? (data.createdAt as { toDate: () => Date }).toDate()
                : new Date((data.createdAt as string | Date) || Date.now());

            const seenBy = Object.entries(data.seenBy || {}).reduce<Record<string, Date>>((acc, [uid, value]) => {
              let parsed: Date | null = null;

              if (value instanceof Date) {
                parsed = value;
              } else if (typeof value === 'string') {
                parsed = new Date(value);
              } else if (value && typeof value === 'object' && typeof value.toDate === 'function') {
                parsed = value.toDate();
              }

              if (parsed && Number.isFinite(parsed.getTime())) {
                acc[uid] = parsed;
              }
              return acc;
            }, {});

            return {
              id: snapshotDoc.id,
              groupId: data.groupId,
              content: data.content || '',
              type: data.type,
              senderId: data.senderId || '',
              senderName: data.senderName,
              createdAt,
              seenBy,
            } as GroupMessagePreview;
          })
          .filter((item): item is GroupMessagePreview => Boolean(item));

        const nextLastByGroup: Record<string, GroupMessagePreview> = {};
        const nextUnreadByGroup: Record<string, number> = {};

        groupMessages.forEach((message) => {
          const existing = nextLastByGroup[message.groupId];
          if (!existing || message.createdAt > existing.createdAt) {
            nextLastByGroup[message.groupId] = message;
          }

          const wasSeen = Boolean(message.seenBy?.[user.uid]);
          if (message.senderId !== user.uid && !wasSeen) {
            nextUnreadByGroup[message.groupId] = (nextUnreadByGroup[message.groupId] || 0) + 1;
          }
        });

        setGroupLastMessageById(nextLastByGroup);
        setGroupUnreadById(nextUnreadByGroup);
        setGroupStreamsError(null);
      },
      (error) => {
        console.error('Failed to listen to group message previews:', error);
        setGroupStreamsError('Failed to sync group message previews.');
      }
    );

    return () => unsubscribe();
  }, [user?.uid, retryTick]);

  useEffect(() => {
    if (!user) {
      setConversationPreferences({});
      return;
    }

    const preferencesRef = collection(db, 'users', user.uid, 'messagePreferences');
    const unsubscribe = onSnapshot(
      preferencesRef,
      (snapshot) => {
        const nextPrefs: Record<string, ConversationPreference> = {};

        snapshot.docs.forEach((snapshotDoc) => {
          const data = snapshotDoc.data() as Partial<ConversationPreference>;
          nextPrefs[snapshotDoc.id] = {
            pinned: Boolean(data.pinned),
            archived: Boolean(data.archived),
          };
        });

        setConversationPreferences(nextPrefs);
      },
      (error) => {
        if (!isPermissionDeniedError(error)) {
          console.error('Failed to listen to conversation preferences:', error);
        }
        setGroupStreamsError(
          isPermissionDeniedError(error)
            ? 'Conversation preferences are unavailable for this account.'
            : 'Failed to sync conversation preferences.'
        );
      }
    );

    return () => unsubscribe();
  }, [user?.uid, retryTick]);

  // Deep-link: /messages?user=<username|uid> (or navigation state { userId })
  useEffect(() => {
    if (!user) return;

    const params = new URLSearchParams(location.search);
    const queryUser = params.get('user');
    const queryGroup = params.get('group');
    const stateUser = (location.state as { userId?: string } | null)?.userId;
    const stateGroup = (location.state as { groupId?: string } | null)?.groupId;

    const targetGroupId =
      (typeof queryGroup === 'string' && queryGroup.trim() ? queryGroup.trim() : undefined) ||
      (typeof stateGroup === 'string' && stateGroup.trim() ? stateGroup.trim() : undefined);

    if (targetGroupId) {
      setSelectedUser(null);
      setSelectedUserInfo(null);
      setSelectedGroupId(targetGroupId);
      return;
    }

    const targetUserKey = (typeof queryUser === 'string' && queryUser.trim() ? queryUser.trim() : undefined) ||
      (typeof stateUser === 'string' && stateUser.trim() ? stateUser.trim() : undefined);

    let cancelled = false;

    (async () => {
      if (!targetUserKey) return;

      try {
        const normalizedKey = targetUserKey.toLowerCase();

        // 1) Resolve locally from friend list by uid or username.
        const friend = friends.find((f) => {
          const friendId = f?.otherUser?.id;
          const friendUsername = (f?.otherUser?.username || '').toLowerCase();
          return friendId === targetUserKey || friendUsername === normalizedKey;
        });

        if (friend?.otherUser) {
          if (cancelled) return;
          setSelectedGroupId(null);
          setSelectedUser(friend.otherUser.id);
          setSelectedUserInfo(friend.otherUser);
          return;
        }

        // 2) Resolve by username slug from users collection.
        const byUsernameSnap = await getDocs(
          query(collection(db, 'users'), where('username', '==', targetUserKey), limit(1))
        );

        if (!byUsernameSnap.empty) {
          const resolved = byUsernameSnap.docs[0];
          const data = resolved.data() as { name?: string; displayName?: string; username?: string; photoURL?: string };
          if (cancelled) return;
          setSelectedGroupId(null);
          setSelectedUser(resolved.id);
          setSelectedUserInfo({
            id: resolved.id,
            name: data?.name || data?.displayName || 'User',
            username: data?.username,
            photoURL: data?.photoURL,
            isOnline: false,
          });
          return;
        }

        // 3) Fallback: treat slug as a uid for backward compatibility.
        const snap = await getDoc(doc(db, 'users', targetUserKey));
        if (!snap.exists() || cancelled) return;
        const data = snap.data() as { name?: string; displayName?: string; username?: string; photoURL?: string };
        setSelectedGroupId(null);
        setSelectedUser(targetUserKey);
        setSelectedUserInfo({
          id: targetUserKey,
          name: data?.name || data?.displayName || 'User',
          username: data?.username,
          photoURL: data?.photoURL,
          isOnline: false,
        });
      } catch {
        // ignore
      }
    })();

    return () => {
      cancelled = true;
    };
    // friends is intentional: once friends load, we can resolve info without extra reads.
  }, [user, location.search, location.state, friends]);

  useEffect(() => {
    if (!selectedGroupId) return;
    if (!groupsLoaded) return;
    if (groupThreads.some((group) => group.id === selectedGroupId)) return;
    setSelectedGroupId(null);
  }, [groupThreads, groupsLoaded, selectedGroupId]);

  const getConversationPreference = (friendId: string): ConversationPreference => {
    return conversationPreferences[friendId] || { pinned: false, archived: false };
  };

  const getConversationKey = (type: 'dm' | 'group', id: string): string => `${type}:${id}`;

  const getConversationUnread = (kind: 'dm' | 'group', id: string): number => {
    if (kind === 'dm') {
      return unreadByUser[id] || 0;
    }
    return groupUnreadById[id] || 0;
  };

  const inboxIntelligence = useMemo(() => {
    const now = Date.now();

    const dmPriority = directConversations
      .map((friend) => {
        const conversationKey = getConversationKey('dm', friend.otherUser.id);
        const preference = getConversationPreference(conversationKey);
        if (preference.archived) return null;

        const unread = unreadByUser[friend.otherUser.id] || 0;
        if (unread === 0) return null;

        const lastAtMs = lastMessageByUser[friend.otherUser.id]?.createdAt?.getTime() || 0;
        const hoursSinceLast = lastAtMs > 0 ? Math.max(0, (now - lastAtMs) / (1000 * 60 * 60)) : 48;
        const recencyWeight = Math.max(0, 12 - hoursSinceLast / 2);
        const pinBoost = preference.pinned ? 2 : 0;
        const score = Math.round(unread * 8 + recencyWeight + pinBoost);

        return {
          id: friend.otherUser.id,
          kind: 'dm' as const,
          label: friend.otherUser.name,
          unread,
          lastAtMs,
          score,
        };
      })
      .filter(Boolean) as PriorityConversation[];

    const groupPriority = groupThreads
      .map((group) => {
        const conversationKey = getConversationKey('group', group.id);
        const preference = getConversationPreference(conversationKey);
        if (preference.archived) return null;

        const unread = groupUnreadById[group.id] || 0;
        if (unread === 0) return null;

        const lastAtMs = groupLastMessageById[group.id]?.createdAt?.getTime() || 0;
        const hoursSinceLast = lastAtMs > 0 ? Math.max(0, (now - lastAtMs) / (1000 * 60 * 60)) : 48;
        const recencyWeight = Math.max(0, 10 - hoursSinceLast / 2);
        const pinBoost = preference.pinned ? 2 : 0;
        const groupBoost = 1;
        const score = Math.round(unread * 7 + recencyWeight + pinBoost + groupBoost);

        return {
          id: group.id,
          kind: 'group' as const,
          label: group.name,
          unread,
          lastAtMs,
          score,
        };
      })
      .filter(Boolean) as PriorityConversation[];

    const allPriority = [...dmPriority, ...groupPriority].sort((a, b) => b.score - a.score);
    const totalUnread = allPriority.reduce((acc, conversation) => acc + conversation.unread, 0);
    const staleUnreadConversations = allPriority.filter((conversation) => {
      if (!conversation.lastAtMs) return true;
      return now - conversation.lastAtMs > 24 * 60 * 60 * 1000;
    }).length;
    const activeUnreadConversations = allPriority.filter((conversation) => {
      if (!conversation.lastAtMs) return false;
      return now - conversation.lastAtMs <= 6 * 60 * 60 * 1000;
    }).length;

    const projectedUnreadIn6h = totalUnread + Math.max(
      0,
      Math.round(activeUnreadConversations * 1.6 + staleUnreadConversations * 0.7)
    );

    const estimatedClearMinutes = Math.max(
      2,
      Math.round(totalUnread * 0.75 + staleUnreadConversations * 1.4 + Math.min(6, allPriority.length))
    );

    const highestImpactConversation = [...allPriority]
      .sort((a, b) => {
        const aStaleBoost = a.lastAtMs && now - a.lastAtMs > 24 * 60 * 60 * 1000 ? 8 : 0;
        const bStaleBoost = b.lastAtMs && now - b.lastAtMs > 24 * 60 * 60 * 1000 ? 8 : 0;
        return b.score + bStaleBoost - (a.score + aStaleBoost);
      })
      .at(0);

    const workloadTrend = projectedUnreadIn6h > totalUnread + 2 ? 'rising' : 'stable';
    const highLoad = totalUnread >= 8 || staleUnreadConversations >= 3;
    const avgRecentProgress = responseSprintHistory.length
      ? responseSprintHistory.slice(0, 10).reduce((sum, item) => sum + item.progressPercent, 0) / Math.min(10, responseSprintHistory.length)
      : 55;
    const coachingBoost = avgRecentProgress >= 70 ? 1 : 0;
    const modeBatchFactor = responseSprintMode === 'rapid' ? 1.25 : responseSprintMode === 'focus' ? 0.8 : 1;
    const modeMinuteFactor = responseSprintMode === 'rapid' ? 1.15 : responseSprintMode === 'focus' ? 0.85 : 1;

    const recommendedBatchSize = Math.max(
      1,
      Math.min(
        6,
        Math.round((highLoad
          ? Math.ceil(totalUnread / 2)
          : Math.ceil(Math.max(1, totalUnread * 0.35))) * modeBatchFactor) + coachingBoost
      )
    );

    const responseSprint: ResponseSprintPlan = {
      recommendedBatchSize,
      expectedUnreadAfterSprint: Math.max(0, totalUnread - recommendedBatchSize),
      estimatedSprintMinutes: Math.max(2, Math.round(recommendedBatchSize * 1.3 * modeMinuteFactor)),
      sprintTargets: allPriority.slice(0, Math.min(4, recommendedBatchSize)),
    };

    return {
      totalUnread,
      staleUnreadConversations,
      activeUnreadConversations,
      projectedUnreadIn6h,
      estimatedClearMinutes,
      workloadTrend,
      highLoad,
      highestImpactConversation,
      responseSprint,
      topPriority: allPriority.slice(0, 3),
    };
  }, [
    directConversations,
    groupLastMessageById,
    groupThreads,
    groupUnreadById,
    lastMessageByUser,
    unreadByUser,
    conversationPreferences,
    responseSprintHistory,
    responseSprintMode,
  ]);

  const filteredFriends = useMemo(() => {
    return directConversations
      .filter((friend) => {
        const matchesSearch =
          friend.otherUser.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (friend.otherUser.username && friend.otherUser.username.toLowerCase().includes(searchQuery.toLowerCase()));

        if (!matchesSearch) return false;

        const preference = getConversationPreference(getConversationKey('dm', friend.otherUser.id));
        if (showArchived) return preference.archived;
        if (showPinned) return preference.pinned && !preference.archived;
        return !preference.archived;
      })
      .sort((a, b) => {
        const aPreference = getConversationPreference(getConversationKey('dm', a.otherUser.id));
        const bPreference = getConversationPreference(getConversationKey('dm', b.otherUser.id));

        if (!showPinned && !showArchived) {
          if (aPreference.pinned !== bPreference.pinned) {
            return aPreference.pinned ? -1 : 1;
          }
        }

        const aLast = lastMessageByUser[a.otherUser.id]?.createdAt?.getTime() || 0;
        const bLast = lastMessageByUser[b.otherUser.id]?.createdAt?.getTime() || 0;
        return bLast - aLast;
      });
  }, [directConversations, lastMessageByUser, searchQuery, showArchived, showPinned, conversationPreferences]);

  const filteredGroupThreads = useMemo(() => {
    return groupThreads
      .filter((group) => {
        const matchesSearch = group.name.toLowerCase().includes(searchQuery.toLowerCase());
        if (!matchesSearch) return false;

        const preference = getConversationPreference(getConversationKey('group', group.id));
        if (showArchived) return preference.archived;
        if (showPinned) return preference.pinned && !preference.archived;
        return !preference.archived;
      })
      .sort((a, b) => {
        const aPreference = getConversationPreference(getConversationKey('group', a.id));
        const bPreference = getConversationPreference(getConversationKey('group', b.id));

        if (!showPinned && !showArchived && aPreference.pinned !== bPreference.pinned) {
          return aPreference.pinned ? -1 : 1;
        }

        const aLast = groupLastMessageById[a.id]?.createdAt?.getTime() || 0;
        const bLast = groupLastMessageById[b.id]?.createdAt?.getTime() || 0;
        return bLast - aLast;
      });
  }, [groupThreads, groupLastMessageById, searchQuery, showArchived, showPinned, conversationPreferences]);

  const updateConversationPreference = async (
    conversationKey: string,
    patch: Partial<ConversationPreference>
  ): Promise<void> => {
    if (!user) return;
    if (updatingConversationId) return;

    setUpdatingConversationId(conversationKey);

    try {
      const prefRef = doc(db, 'users', user.uid, 'messagePreferences', conversationKey);
      await setDoc(
        prefRef,
        {
          ...patch,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Failed to update conversation preference:', error);
      toast({
        title: 'Update failed',
        description: 'Could not update conversation preference. Try again.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingConversationId(null);
    }
  };

  const handleCreateGroup = async () => {
    if (creatingGroup) return;
    if (!user) return;
    if (!groupName.trim()) {
      toast({
        title: 'Group name required',
        description: 'Enter a name before creating a group chat.',
        variant: 'destructive',
      });
      return;
    }

    const uniqueParticipants = Array.from(new Set([user.uid, ...selectedGroupMemberIds]));
    if (uniqueParticipants.length < 3) {
      toast({
        title: 'Not enough members',
        description: 'Select at least two friends to create a group chat.',
        variant: 'destructive',
      });
      return;
    }

    const participantNames: Record<string, string> = {
      [user.uid]: user.displayName || 'You',
    };

    friends.forEach((friend) => {
      if (uniqueParticipants.includes(friend.otherUser.id)) {
        participantNames[friend.otherUser.id] = friend.otherUser.name;
      }
    });

    try {
      setCreatingGroup(true);

      const groupRef = await addDoc(collection(db, 'messageGroups'), {
        name: groupName.trim(),
        createdBy: user.uid,
        participants: uniqueParticipants,
        participantNames,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setIsCreateGroupOpen(false);
      setGroupName('');
      setSelectedGroupMemberIds([]);
      navigate(`/messages?group=${encodeURIComponent(groupRef.id)}`);
    } catch (error) {
      console.error('Failed to create group chat:', error);
      toast({
        title: 'Create group failed',
        description: 'Could not create group chat. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCreatingGroup(false);
    }
  };

  const toggleGroupMember = (friendId: string) => {
    setSelectedGroupMemberIds((prev) =>
      prev.includes(friendId) ? prev.filter((id) => id !== friendId) : [...prev, friendId]
    );
  };

  const selectedGroup = selectedGroupId
    ? groupThreads.find((group) => group.id === selectedGroupId) || null
    : null;

  const getGroupParticipantPreview = (group: GroupThread): string => {
    const names = group.participants
      .map((participantId) => {
        if (participantId === user?.uid) return 'You';
        if (group.participantNames?.[participantId]) return group.participantNames[participantId];
        const friend = friends.find((item) => item.otherUser.id === participantId);
        return friend?.otherUser.name || 'Member';
      })
      .filter(Boolean);

    if (names.length <= 3) {
      return names.join(', ');
    }

    const shown = names.slice(0, 3).join(', ');
    return `${shown} +${names.length - 3}`;
  };

  const getGroupPreview = (groupId: string): string => {
    const last = groupLastMessageById[groupId];
    if (!last) return 'Start the group conversation';

    if (last.type && last.type !== 'text') {
      return 'Sent a media message';
    }

    return last.content || 'New message';
  };

  const formatLastMessageTime = (date: Date): string => {
    const now = Date.now();
    const diffMs = Math.max(0, now - date.getTime());
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    if (diffMs < minute) return 'now';
    if (diffMs < hour) return `${Math.floor(diffMs / minute)}m`;
    if (diffMs < day) return `${Math.floor(diffMs / hour)}h`;
    return `${Math.floor(diffMs / day)}d`;
  };

  const getMessagePreview = (friendUserId: string): string => {
    const last = lastMessageByUser[friendUserId];
    if (!last) return t('messages.startAConversation');

    if (last.type && last.type !== 'text') {
      return t('messages.mediaMessage', 'Sent a media message');
    }

    if (!last.content.trim()) {
      return t('messages.emptyMessage', 'New message');
    }

    return last.content;
  };

  const handleUserSelect = (friend: FriendRecord) => {
    setSelectedGroupId(null);
    setSelectedUser(friend.otherUser.id);
    setSelectedUserInfo(friend.otherUser);
    const userSlug = (friend.otherUser.username || '').trim() || friend.otherUser.id;
    navigate(`/messages?user=${encodeURIComponent(userSlug)}`);
  };

  const handleGroupSelect = (groupId: string) => {
    setSelectedUser(null);
    setSelectedUserInfo(null);
    setSelectedGroupId(groupId);
    navigate(`/messages?group=${encodeURIComponent(groupId)}`);
  };

  const jumpToPriorityConversation = (conversation: PriorityConversation) => {
    if (conversation.kind === 'dm') {
      const friend = friends.find((item) => item.otherUser.id === conversation.id);
      if (friend) {
        handleUserSelect(friend);
      } else {
        navigate(`/messages?user=${encodeURIComponent(conversation.id)}`);
      }
      return;
    }

    handleGroupSelect(conversation.id);
  };

  const responseSprintProgress = useMemo(() => {
    if (!activeResponseSprint) {
      return null;
    }

    const parsedTargets = activeResponseSprint.targetKeys
      .map((key) => {
        const [kind, id] = key.split(':');
        if (!id || (kind !== 'dm' && kind !== 'group')) {
          return null;
        }
        return { kind: kind as 'dm' | 'group', id, key };
      })
      .filter((item): item is { kind: 'dm' | 'group'; id: string; key: string } => Boolean(item));

    const initialUnreadTotal = parsedTargets.reduce(
      (sum, target) => sum + (activeResponseSprint.baselineUnreadByKey[target.key] || 0),
      0
    );

    const currentUnreadTotal = parsedTargets.reduce(
      (sum, target) => sum + getConversationUnread(target.kind, target.id),
      0
    );

    const reducedUnread = Math.max(0, initialUnreadTotal - currentUnreadTotal);
    const progressPercent = initialUnreadTotal > 0
      ? Math.min(100, Math.round((reducedUnread / initialUnreadTotal) * 100))
      : 100;

    const completedTargets = parsedTargets.filter((target) => getConversationUnread(target.kind, target.id) === 0).length;
    const nextTarget = parsedTargets
      .map((target) => ({
        ...target,
        unread: getConversationUnread(target.kind, target.id),
      }))
      .filter((target) => target.unread > 0)
      .sort((a, b) => b.unread - a.unread)
      .at(0);

    return {
      initialUnreadTotal,
      currentUnreadTotal,
      reducedUnread,
      progressPercent,
      completedTargets,
      totalTargets: parsedTargets.length,
      nextTarget,
    };
  }, [activeResponseSprint, groupUnreadById, unreadByUser]);

  const responseSprintInsights = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const history = responseSprintHistory
      .slice()
      .sort((a, b) => b.endedAt - a.endedAt);

    const todaySprints = history.filter((item) => item.endedAt >= startOfToday);
    const todayClearedUnread = todaySprints.reduce((sum, item) => sum + item.reducedUnread, 0);
    const avgSprintEfficiency = history.length
      ? Math.round(history.reduce((sum, item) => sum + item.progressPercent, 0) / history.length)
      : 0;
    const avgUnreadCleared = history.length
      ? Math.round(history.reduce((sum, item) => sum + item.reducedUnread, 0) / history.length)
      : 0;

    const uniqueDays = Array.from(
      new Set(
        history.map((item) => {
          const d = new Date(item.endedAt);
          return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        })
      )
    );

    let streakDays = 0;
    for (let offset = 0; offset < 30; offset += 1) {
      const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset);
      const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
      if (!uniqueDays.includes(key)) {
        break;
      }
      streakDays += 1;
    }

    return {
      totalSprints: history.length,
      todaySprints: todaySprints.length,
      todayClearedUnread,
      avgSprintEfficiency,
      avgUnreadCleared,
      streakDays,
      performanceScore: Math.min(100, Math.round(
        (avgSprintEfficiency * 0.55) +
        (Math.min(20, avgUnreadCleared * 2) * 1.5) +
        (Math.min(10, streakDays) * 1.5)
      )),
      lastSprintAt: history[0]?.endedAt || null,
    };
  }, [responseSprintHistory]);

  const responseSprintCoach = useMemo(() => {
    const shortRecent = responseSprintHistory.slice(0, 5);
    const shortAvgProgress = shortRecent.length
      ? shortRecent.reduce((sum, item) => sum + item.progressPercent, 0) / shortRecent.length
      : 55;
    const shortAvgDuration = shortRecent.length
      ? shortRecent.reduce((sum, item) => sum + item.durationMinutes, 0) / shortRecent.length
      : 6;

    let recommendedMode: ResponseSprintMode = 'balanced';
    if (inboxIntelligence.highLoad && shortAvgProgress < 58) {
      recommendedMode = 'focus';
    } else if (inboxIntelligence.highLoad && shortAvgProgress >= 75) {
      recommendedMode = 'rapid';
    } else if (!inboxIntelligence.highLoad && shortAvgProgress >= 80) {
      recommendedMode = 'rapid';
    }

    const riskScore =
      (inboxIntelligence.staleUnreadConversations * 12) +
      (inboxIntelligence.projectedUnreadIn6h > inboxIntelligence.totalUnread ? 14 : 0) +
      (responseSprintMode === 'rapid' ? 8 : 0) +
      (shortAvgDuration >= 10 ? 6 : 0) -
      (shortAvgProgress >= 75 ? 10 : 0);

    const riskLevel: SprintRiskLevel = riskScore >= 38 ? 'high' : riskScore >= 20 ? 'moderate' : 'low';

    const activeMinutes = activeResponseSprint
      ? Math.max(1, Math.round((Date.now() - activeResponseSprint.startedAt) / (1000 * 60)))
      : 0;

    const paceHint = !activeResponseSprint
      ? 'Start a sprint to get live pacing guidance.'
      : riskLevel === 'high' && activeMinutes >= 8
        ? 'Take a 2-minute reset after this target to avoid burnout.'
        : responseSprintMode === 'rapid' && activeMinutes >= 6
          ? 'Great pace. Finish one more target, then reassess mode.'
          : 'Maintain steady replies; prioritize stale threads first.';

    return {
      recommendedMode,
      riskLevel,
      riskScore: Math.max(0, Math.min(100, Math.round(riskScore))),
      activeMinutes,
      paceHint,
    };
  }, [
    activeResponseSprint,
    inboxIntelligence.highLoad,
    inboxIntelligence.projectedUnreadIn6h,
    inboxIntelligence.staleUnreadConversations,
    inboxIntelligence.totalUnread,
    responseSprintHistory,
    responseSprintMode,
  ]);

  const finalizeResponseSprint = () => {
    if (!activeResponseSprint || !responseSprintProgress) {
      setActiveResponseSprint(null);
      return;
    }

    const now = Date.now();
    const durationMinutes = Math.max(1, Math.round((now - activeResponseSprint.startedAt) / (1000 * 60)));
    const historyItem: ResponseSprintHistoryItem = {
      id: `${now}-${Math.round(Math.random() * 100000)}`,
      endedAt: now,
      durationMinutes,
      initialUnreadTotal: responseSprintProgress.initialUnreadTotal,
      reducedUnread: responseSprintProgress.reducedUnread,
      progressPercent: responseSprintProgress.progressPercent,
      completedTargets: responseSprintProgress.completedTargets,
      totalTargets: responseSprintProgress.totalTargets,
    };

    setResponseSprintHistory((prev) => [historyItem, ...prev].slice(0, 30));
    setActiveResponseSprint(null);
  };

  const startResponseSprint = () => {
    const sprintTargets = inboxIntelligence.responseSprint.sprintTargets;
    if (!sprintTargets.length) {
      return;
    }

    const targetKeys = sprintTargets.map((target) => getConversationKey(target.kind, target.id));
    const baselineUnreadByKey = sprintTargets.reduce<Record<string, number>>((acc, target) => {
      const key = getConversationKey(target.kind, target.id);
      acc[key] = getConversationUnread(target.kind, target.id);
      return acc;
    }, {});

    setActiveResponseSprint({
      startedAt: Date.now(),
      targetKeys,
      baselineUnreadByKey,
    });
  };

  const stopResponseSprint = () => {
    finalizeResponseSprint();
  };

  useEffect(() => {
    if (!activeResponseSprint || !responseSprintProgress) {
      return;
    }

    if (responseSprintProgress.progressPercent >= 100 || responseSprintProgress.currentUnreadTotal === 0) {
      finalizeResponseSprint();
    }
  }, [activeResponseSprint, responseSprintProgress]);

  const handleAudioCall = () => {
    if (!selectedUserInfo) return;

    startCall(
      selectedUserInfo.id,
      selectedUserInfo.name,
      selectedUserInfo.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${selectedUserInfo.name}`,
      'audio'
    );
  };

  const handleVideoCall = () => {
    if (!selectedUserInfo) return;

    startCall(
      selectedUserInfo.id,
      selectedUserInfo.name,
      selectedUserInfo.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${selectedUserInfo.name}`,
      'video'
    );
  };

  const getActiveCallPeerId = (): string | null => {
    if (!currentCall || !user) return null;
    return currentCall.caller.id === user.uid ? currentCall.receiver.id : currentCall.caller.id;
  };

  const isSelectedConversationInActiveCall = Boolean(
    selectedUserInfo && isInCall && getActiveCallPeerId() === selectedUserInfo.id
  );

  const isOtherConversationActive = Boolean(
    isInCall && selectedUserInfo && getActiveCallPeerId() !== selectedUserInfo.id
  );

  const handleScreenShare = async () => {
    if (!selectedUserInfo) return;

    if (isSelectedConversationInActiveCall) {
      await toggleScreenShare();
      return;
    }

    await startScreenShareCall(
      selectedUserInfo.id,
      selectedUserInfo.name,
      selectedUserInfo.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${selectedUserInfo.name}`
    );
  };

  const handleRetryConversations = () => {
    setLoading(true);
    setGroupStreamsError(null);
    setRetryTick((prev) => prev + 1);
  };

  const openDirectMessageAICopilot = () => {
    const chatPartnerName = selectedUserInfo?.name || 'Direct Message';
    setAiChatTitle(chatPartnerName);
    if (selectedUser && messages && messages.length > 0) {
      const threadMsgs = messages
        .filter((m) => m.participants?.includes(selectedUser))
        .slice(0, 12)
        .map((m) => `${m.senderId === user?.uid ? 'Me' : chatPartnerName}: ${m.content}`)
        .reverse()
        .join('\n');
      setAiInitialSnippet(threadMsgs);
    } else {
      setAiInitialSnippet('');
    }
    setIsAICopilotOpen(true);
  };

  const openGroupAICopilot = () => {
    const name = selectedGroup?.name || 'Group Chat';
    setAiChatTitle(name);
    if (selectedGroup && messages && messages.length > 0) {
      const threadMsgs = messages
        .filter((m) => m.participants?.includes(selectedGroup.id) || (selectedGroup.participants && selectedGroup.participants.includes(m.senderId)))
        .slice(0, 12)
        .map((m) => `${m.senderId === user?.uid ? 'Me' : 'Participant'}: ${m.content}`)
        .reverse()
        .join('\n');
      setAiInitialSnippet(threadMsgs);
    } else {
      setAiInitialSnippet('');
    }
    setIsAICopilotOpen(true);
  };

  const openGeneralAICopilot = () => {
    if (selectedUserInfo) {
      openDirectMessageAICopilot();
    } else if (selectedGroup) {
      openGroupAICopilot();
    } else {
      setAiChatTitle('Inbox Intelligence');
      setAiInitialSnippet(`Total unread messages: ${inboxIntelligence.totalUnread}. Stale conversations: ${inboxIntelligence.staleUnreadConversations}. Next best action: ${inboxIntelligence.highestImpactConversation?.label || 'Inbox up to date'}.`);
      setIsAICopilotOpen(true);
    }
  };

  if (!user) {
    return (
      <div className="h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 to-cyan-100 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center">
        <Card className="motion-enter reveal-2 p-12 text-center shadow-2xl border border-blue-100/80 bg-white/82 dark:bg-slate-900/72 dark:text-slate-100 backdrop-blur-md">
          <MessageCircle className="w-16 h-16 mx-auto mb-4 text-blue-500" />
          <h2 className="text-2xl font-bold text-gray-800 dark:text-slate-100 mb-2">{t('messages.title')}</h2>
          <p className="text-gray-600 dark:text-slate-300 mb-6">{t('messages.signInRequired')}</p>
          <Button onClick={() => navigate('/')} className="bg-blue-500 hover:bg-blue-600">
            {t('common.login')}
          </Button>
        </Card>
      </div>
    );
  }

  if (loading || friendsLoading || messagesLoading) {
    return (
      <div className="h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 to-cyan-100 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center">
        <Card className="motion-enter reveal-2 p-12 text-center shadow-2xl border border-blue-100/80 bg-white/82 dark:bg-slate-900/72 dark:text-slate-100 backdrop-blur-md">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-2">{t('common.loading')}</h3>
          <p className="text-gray-600 dark:text-slate-300">{t('messages.loadingConversations')}</p>
        </Card>
      </div>
    );
  }

  if (error || messagesError) {
    return (
      <div className="h-[calc(100vh-4rem)] bg-gradient-to-br from-red-50 to-orange-100 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center">
        <Card className="motion-enter reveal-2 p-12 text-center shadow-2xl border border-red-100/80 bg-white/82 dark:bg-slate-900/72 dark:text-slate-100 backdrop-blur-md">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-950/40 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-2">{t('common.error')}</h3>
          <p className="text-red-600 mb-6">{error || messagesError}</p>
          <Button 
            onClick={handleRetryConversations}
            className="bg-red-500 hover:bg-red-600"
          >
            {t('common.try_again')}
          </Button>
        </Card>
      </div>
    );
  }

  if (directConversations.length === 0 && groupThreads.length === 0) {
    return (
      <div className="h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 to-cyan-100 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center">
        <Card className="motion-enter reveal-2 p-12 text-center shadow-2xl border border-blue-100/80 bg-white/82 dark:bg-slate-900/72 dark:text-slate-100 backdrop-blur-md max-w-md">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 dark:text-slate-100 mb-3">No Conversations Yet</h3>
          <p className="text-gray-600 dark:text-slate-300 mb-4">{t('messages.noMessages')}</p>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">Connect with friends to start messaging!</p>
          <Button 
            onClick={() => navigate('/friends')}
            className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
          >
            Find Friends
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] bg-gradient-to-br from-slate-50 to-blue-50/90 dark:from-slate-950 dark:to-slate-900 overflow-hidden motion-enter reveal-1">
      <div className="flex h-full overflow-hidden">
        {/* Sidebar - Hidden on mobile when chat selected */}
        <div className={cn(
          "bg-white/88 dark:bg-slate-900/82 border-r border-blue-100/80 dark:border-slate-700/80 shadow-[0_20px_45px_-34px_rgba(15,23,42,0.55)] backdrop-blur-md flex-shrink-0 overflow-hidden flex flex-col transition-all duration-300",
          "w-full sm:w-72 md:w-80",
          (selectedUser || selectedGroupId) ? "hidden sm:flex" : "flex"
        )}>
          {/* Header */}
          <div className="p-3 sm:p-5 border-b border-blue-100/80 dark:border-slate-700/80 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-slate-900 dark:to-slate-900 flex-shrink-0">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-700 to-cyan-600 bg-clip-text text-transparent flex items-center gap-2">
                <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                {t('messages.title')}
              </h1>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openGeneralAICopilot}
                  className="h-8 px-2 sm:px-2.5 text-xs border-blue-200 dark:border-blue-900/60 bg-white/70 dark:bg-slate-800/70 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg shadow-sm flex items-center gap-1.5"
                  title="DeepSeek AI Conversation Intelligence & Copilot"
                >
                  <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                  <span className="hidden xs:inline font-medium">AI Copilot</span>
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setIsCreateGroupOpen(true)}
                  className="hover:bg-white/60 dark:hover:bg-slate-800 transition-colors rounded-full w-8 h-8 sm:w-9 sm:h-9"
                  title="Create group chat"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-slate-300" />
                </Button>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="hover:bg-white/60 dark:hover:bg-slate-800 transition-colors rounded-full w-8 h-8 sm:w-9 sm:h-9"
                >
                  <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-slate-300" />
                </Button>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500 w-4 h-4" />
              <Input
                placeholder={t('messages.searchConversations')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-700 focus:border-blue-300 focus:ring-blue-200 dark:text-slate-100 rounded-xl shadow-sm text-sm"
              />
            </div>
          </div>

          {/* Filter tabs */}
          <div className="px-2 sm:px-4 py-2 sm:py-3 border-b border-gray-100 dark:border-slate-800 flex-shrink-0 bg-gray-50/50 dark:bg-slate-950/40">
            <div className="flex gap-1 sm:gap-2">
              <Button 
                variant={!showPinned && !showArchived ? "default" : "ghost"}
                size="sm"
                onClick={() => { setShowPinned(false); setShowArchived(false); }}
                className={cn(
                  "flex-1 rounded-lg transition-all duration-200 text-xs sm:text-sm px-2 sm:px-3",
                  !showPinned && !showArchived 
                    ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-sm" 
                    : "hover:bg-gray-100 dark:hover:bg-slate-800 dark:text-slate-200"
                )}
              >
                {t('common.all')}
              </Button>
              <Button 
                variant={showPinned ? "default" : "ghost"}
                size="sm"
                onClick={() => { setShowPinned(true); setShowArchived(false); }}
                className={cn(
                  "flex-1 rounded-lg transition-all duration-200 text-xs sm:text-sm px-2 sm:px-3",
                  showPinned 
                    ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-sm" 
                    : "hover:bg-gray-100 dark:hover:bg-slate-800 dark:text-slate-200"
                )}
              >
                <Pin className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
                <span className="hidden xs:inline">{t('messages.pinned')}</span>
              </Button>
              <Button 
                variant={showArchived ? "default" : "ghost"}
                size="sm"
                onClick={() => { setShowPinned(false); setShowArchived(true); }}
                className={cn(
                  "flex-1 rounded-lg transition-all duration-200 text-xs sm:text-sm px-2 sm:px-3",
                  showArchived 
                    ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-sm" 
                    : "hover:bg-gray-100 dark:hover:bg-slate-800 dark:text-slate-200"
                )}
              >
                <Archive className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
                <span className="hidden xs:inline">{t('messages.archived')}</span>
              </Button>
            </div>

            {groupStreamsError ? (
              <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-950/30 dark:border-amber-900/50 dark:text-amber-300 flex items-center justify-between gap-2">
                <span className="truncate">{groupStreamsError}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-[11px] text-amber-800 dark:text-amber-300"
                  onClick={handleRetryConversations}
                >
                  Retry
                </Button>
              </div>
            ) : null}

            {inboxIntelligence.totalUnread > 0 ? (
              <div className="mt-2 rounded-xl border border-blue-200/80 dark:border-blue-900/50 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 px-2.5 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-300" />
                    <p className="text-[11px] font-semibold text-blue-700 dark:text-blue-200 uppercase tracking-wide">
                      Inbox Intelligence
                    </p>
                  </div>
                  <span className="text-[11px] font-medium text-blue-700 dark:text-blue-200">
                    {inboxIntelligence.totalUnread} unread
                  </span>
                </div>

                <div className="mt-1.5 flex items-center gap-2 text-[11px] text-blue-700/90 dark:text-blue-200/90">
                  <Gauge className="w-3.5 h-3.5" />
                  <span>{inboxIntelligence.staleUnreadConversations} stale conversations</span>
                  {inboxIntelligence.highLoad ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 px-1.5 py-0.5">
                      <AlertTriangle className="w-3 h-3" />
                      High load
                    </span>
                  ) : null}
                </div>

                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                  <span className="inline-flex items-center gap-1 rounded-full border border-blue-200/80 dark:border-blue-900/50 bg-white/80 dark:bg-slate-900/60 px-1.5 py-0.5 text-blue-700 dark:text-blue-200">
                    <TrendingUp className="w-3 h-3" />
                    6h forecast: {inboxIntelligence.projectedUnreadIn6h}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-blue-200/80 dark:border-blue-900/50 bg-white/80 dark:bg-slate-900/60 px-1.5 py-0.5 text-blue-700 dark:text-blue-200">
                    <Clock3 className="w-3 h-3" />
                    Clear in ~{inboxIntelligence.estimatedClearMinutes}m
                  </span>
                  <span className="inline-flex items-center rounded-full border border-blue-200/80 dark:border-blue-900/50 bg-white/80 dark:bg-slate-900/60 px-1.5 py-0.5 text-blue-700 dark:text-blue-200 capitalize">
                    Trend: {inboxIntelligence.workloadTrend}
                  </span>
                </div>

                {inboxIntelligence.highestImpactConversation ? (
                  <button
                    type="button"
                    onClick={() => jumpToPriorityConversation(inboxIntelligence.highestImpactConversation)}
                    className="mt-2 w-full flex items-center justify-between rounded-lg border border-indigo-200/70 dark:border-indigo-900/50 bg-indigo-50/70 dark:bg-indigo-950/20 px-2 py-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                  >
                    <span className="text-[11px] text-indigo-700 dark:text-indigo-200 truncate text-left">
                      Next best: {inboxIntelligence.highestImpactConversation.label}
                    </span>
                    <span className="ml-2 text-[10px] font-semibold text-indigo-700 dark:text-indigo-200">
                      Score {inboxIntelligence.highestImpactConversation.score}
                    </span>
                  </button>
                ) : null}

                {inboxIntelligence.responseSprint.sprintTargets.length > 0 ? (
                  <div className="mt-2 rounded-lg border border-emerald-200/70 dark:border-emerald-900/50 bg-emerald-50/70 dark:bg-emerald-950/20 px-2 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-200">
                        Response sprint
                      </p>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-emerald-700/90 dark:text-emerald-200/90">
                          ~{inboxIntelligence.responseSprint.estimatedSprintMinutes}m
                        </span>
                        {activeResponseSprint ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={stopResponseSprint}
                            className="h-5 px-1.5 text-[10px] text-emerald-700 dark:text-emerald-200"
                          >
                            End
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={startResponseSprint}
                            className="h-5 px-1.5 text-[10px] text-emerald-700 dark:text-emerald-200"
                          >
                            Start
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="mt-1 text-[10px] text-emerald-700/90 dark:text-emerald-200/90">
                      Reply to {inboxIntelligence.responseSprint.recommendedBatchSize} now to drop unread to ~{inboxIntelligence.responseSprint.expectedUnreadAfterSprint}.
                    </p>

                    <div className="mt-1.5 flex flex-wrap gap-1">
                      <span className="inline-flex items-center rounded-full border border-emerald-200 dark:border-emerald-900/50 bg-white/80 dark:bg-slate-900/60 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-200">
                        Mode {responseSprintMode}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-emerald-200 dark:border-emerald-900/50 bg-white/80 dark:bg-slate-900/60 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-200">
                        Recommend {responseSprintCoach.recommendedMode}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-emerald-200 dark:border-emerald-900/50 bg-white/80 dark:bg-slate-900/60 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-200">
                        Today {responseSprintInsights.todaySprints} sprint(s)
                      </span>
                      <span className="inline-flex items-center rounded-full border border-emerald-200 dark:border-emerald-900/50 bg-white/80 dark:bg-slate-900/60 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-200">
                        Cleared {responseSprintInsights.todayClearedUnread} unread
                      </span>
                      <span className="inline-flex items-center rounded-full border border-emerald-200 dark:border-emerald-900/50 bg-white/80 dark:bg-slate-900/60 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-200">
                        Streak {responseSprintInsights.streakDays}d
                      </span>
                      <span className="inline-flex items-center rounded-full border border-emerald-200 dark:border-emerald-900/50 bg-white/80 dark:bg-slate-900/60 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-200">
                        Avg {responseSprintInsights.avgSprintEfficiency}%
                      </span>
                      <span className="inline-flex items-center rounded-full border border-emerald-200 dark:border-emerald-900/50 bg-white/80 dark:bg-slate-900/60 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-200">
                        Score {responseSprintInsights.performanceScore}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-emerald-200 dark:border-emerald-900/50 bg-white/80 dark:bg-slate-900/60 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-200 capitalize">
                        Risk {responseSprintCoach.riskLevel}
                      </span>
                    </div>

                    <div className="mt-1 flex flex-wrap gap-1">
                      {(['focus', 'balanced', 'rapid'] as ResponseSprintMode[]).map((mode) => (
                        <Button
                          key={`response-sprint-mode-${mode}`}
                          type="button"
                          variant={responseSprintMode === mode ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setResponseSprintMode(mode)}
                          className="h-5 px-1.5 text-[10px] capitalize"
                        >
                          {mode}
                        </Button>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-5 px-1.5 text-[10px]"
                        disabled={responseSprintMode === responseSprintCoach.recommendedMode}
                        onClick={() => setResponseSprintMode(responseSprintCoach.recommendedMode)}
                      >
                        Apply coach mode
                      </Button>
                    </div>

                    <div className="mt-1 rounded-md border border-emerald-200/70 dark:border-emerald-900/40 bg-white/80 dark:bg-slate-900/60 px-1.5 py-1 text-[10px] text-emerald-700/95 dark:text-emerald-200/90">
                      {responseSprintCoach.paceHint}
                    </div>

                    {activeResponseSprint && responseSprintProgress ? (
                      <div className="mt-1.5 rounded-md border border-emerald-200/70 dark:border-emerald-900/40 bg-white/80 dark:bg-slate-900/60 px-1.5 py-1.5">
                        <div className="flex items-center justify-between text-[10px] text-emerald-700 dark:text-emerald-200">
                          <span>
                            Progress {responseSprintProgress.progressPercent}%
                          </span>
                          <span>
                            {responseSprintProgress.completedTargets}/{responseSprintProgress.totalTargets} chats clear
                          </span>
                        </div>
                        <div className="mt-1 h-1.5 w-full rounded-full bg-emerald-100 dark:bg-emerald-900/40 overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 transition-all duration-300"
                            style={{ width: `${responseSprintProgress.progressPercent}%` }}
                          />
                        </div>
                        <div className="mt-1 text-[10px] text-emerald-700/90 dark:text-emerald-200/90">
                          Cleared {responseSprintProgress.reducedUnread} / {responseSprintProgress.initialUnreadTotal} unread
                        </div>
                        {responseSprintProgress.nextTarget ? (
                          <button
                            type="button"
                            onClick={() => {
                              jumpToPriorityConversation({
                                id: responseSprintProgress.nextTarget.id,
                                kind: responseSprintProgress.nextTarget.kind,
                                label: responseSprintProgress.nextTarget.kind === 'dm'
                                  ? friends.find((item) => item.otherUser.id === responseSprintProgress.nextTarget?.id)?.otherUser?.name || 'Conversation'
                                  : groupThreads.find((item) => item.id === responseSprintProgress.nextTarget?.id)?.name || 'Group conversation',
                                unread: responseSprintProgress.nextTarget.unread,
                                lastAtMs: 0,
                                score: responseSprintProgress.nextTarget.unread,
                              });
                            }}
                            className="mt-1 w-full text-left rounded-md border border-emerald-200 dark:border-emerald-900/40 px-1.5 py-1 text-[10px] text-emerald-700 dark:text-emerald-200 hover:bg-emerald-50/70 dark:hover:bg-emerald-900/20"
                          >
                            Continue with next target ({responseSprintProgress.nextTarget.unread} unread)
                          </button>
                        ) : null}
                      </div>
                    ) : null}

                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {inboxIntelligence.responseSprint.sprintTargets.map((conversation) => (
                        <button
                          key={`sprint-${conversation.kind}-${conversation.id}`}
                          type="button"
                          onClick={() => jumpToPriorityConversation(conversation)}
                          className="inline-flex items-center gap-1 rounded-full border border-emerald-200 dark:border-emerald-900/60 bg-white/90 dark:bg-slate-900/70 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-200 hover:bg-white dark:hover:bg-slate-800"
                        >
                          <span className="max-w-[110px] truncate">{conversation.label}</span>
                          <span className="inline-flex items-center rounded-full bg-red-500 text-white px-1 py-0.5 leading-none">
                            {conversation.unread}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {inboxIntelligence.topPriority.length > 0 ? (
                  <div className="mt-2 space-y-1.5">
                    {inboxIntelligence.topPriority.map((conversation) => (
                      <button
                        key={`${conversation.kind}-${conversation.id}`}
                        type="button"
                        onClick={() => jumpToPriorityConversation(conversation)}
                        className="w-full flex items-center justify-between rounded-lg border border-blue-200/60 dark:border-blue-900/50 bg-white/80 dark:bg-slate-900/70 px-2 py-1.5 hover:bg-white dark:hover:bg-slate-800 transition-colors"
                      >
                        <span className="truncate text-[11px] text-slate-700 dark:text-slate-200 text-left">
                          {conversation.label}
                        </span>
                        <span className="ml-2 inline-flex items-center rounded-full bg-red-500 text-white text-[10px] px-1.5 py-0.5 font-semibold">
                          {conversation.unread}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-2 sm:p-3">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider px-2 sm:px-3 py-2 mb-1">
                {filteredFriends.length} {t('messages.conversations')}
              </div>
              
              {filteredFriends.map((friend) => {
                const isSelected = selectedUser === friend.otherUser.id;
                const unreadForUser = unreadByUser[friend.otherUser.id] || 0;
                const lastMessage = lastMessageByUser[friend.otherUser.id];
                const conversationKey = getConversationKey('dm', friend.otherUser.id);
                const preference = getConversationPreference(conversationKey);
                const isPinnedConversation = preference.pinned;
                const isArchivedConversation = preference.archived;
                const rowUpdating = updatingConversationId === conversationKey;
                
                return (
                  <button
                    key={friend.id}
                    className={cn(
                      "w-full flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3.5 rounded-xl transition-all duration-200 mb-1 sm:mb-1.5 group relative overflow-hidden",
                      isSelected 
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 shadow-lg scale-[1.02]' 
                        : 'hover:bg-gray-50/80 dark:hover:bg-slate-800/70 hover:shadow-sm'
                    )}
                    onClick={() => handleUserSelect(friend)}
                  >
                    {isSelected && (
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    )}
                    
                    <div className="relative flex-shrink-0">
                      <div className="relative">
                        <img 
                          src={friend.otherUser.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${friend.otherUser.name}`} 
                          alt={friend.otherUser.name}
                          className="w-10 h-10 sm:w-14 sm:h-14 rounded-full object-cover ring-2 ring-white dark:ring-slate-800 shadow-md"
                        />
                        <div className="absolute -bottom-0.5 -right-0.5">
                          <OnlineStatusIndicator userId={friend.otherUser.id} size="md" showLabel={false} showLastSeen={false} />
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex-1 text-left min-w-0 relative z-10">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <h3 className={cn(
                          "font-semibold text-sm sm:text-[15px] truncate",
                          isSelected ? 'text-white' : 'text-gray-900 dark:text-slate-100'
                        )}>
                          {friend.otherUser.name}
                        </h3>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void updateConversationPreference(conversationKey, { pinned: !isPinnedConversation });
                            }}
                            disabled={rowUpdating}
                            className="inline-flex items-center justify-center rounded-full p-1 hover:bg-black/5 dark:hover:bg-white/10"
                            aria-label={isPinnedConversation ? 'Unpin conversation' : 'Pin conversation'}
                          >
                            <Star className={cn(
                              "w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 transition-all duration-200",
                              isPinnedConversation
                                ? 'text-yellow-400 fill-yellow-400'
                                : isSelected
                                  ? 'text-yellow-300'
                                  : 'text-gray-300 group-hover:text-yellow-400'
                            )} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void updateConversationPreference(conversationKey, { archived: !isArchivedConversation });
                            }}
                            disabled={rowUpdating}
                            className="inline-flex items-center justify-center rounded-full p-1 hover:bg-black/5 dark:hover:bg-white/10"
                            aria-label={isArchivedConversation ? 'Restore conversation' : 'Archive conversation'}
                          >
                            {isArchivedConversation ? (
                              <ArchiveRestore className={cn(
                                "w-3 h-3 sm:w-4 sm:h-4",
                                isSelected ? 'text-blue-100' : 'text-gray-400 dark:text-slate-400'
                              )} />
                            ) : (
                              <Archive className={cn(
                                "w-3 h-3 sm:w-4 sm:h-4",
                                isSelected ? 'text-blue-100' : 'text-gray-400 dark:text-slate-400'
                              )} />
                            )}
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between gap-2">
                        <p className={cn(
                          "text-xs sm:text-[13px] truncate font-medium",
                          isSelected ? 'text-blue-100' : 'text-gray-500 dark:text-slate-400'
                        )}>
                          {getMessagePreview(friend.otherUser.id)}
                        </p>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {lastMessage ? (
                            <span className={cn(
                              "text-[11px]",
                              isSelected ? 'text-blue-100' : 'text-gray-400 dark:text-slate-500'
                            )}>
                              {formatLastMessageTime(lastMessage.createdAt)}
                            </span>
                          ) : null}
                          {unreadForUser > 0 ? (
                            <span className="bg-red-500 text-white text-[10px] rounded-full min-w-[1.1rem] h-[1.1rem] px-1.5 inline-flex items-center justify-center font-semibold">
                              {unreadForUser > 9 ? '9+' : unreadForUser}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}

              {filteredGroupThreads.length > 0 ? (
                <>
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider px-2 sm:px-3 py-2 mt-3 mb-1 flex items-center gap-2">
                    <UsersRound className="w-3.5 h-3.5" />
                    Group Chats ({filteredGroupThreads.length})
                  </div>

                  {filteredGroupThreads.map((group) => {
                    const isSelected = selectedGroupId === group.id;
                    const unreadForGroup = groupUnreadById[group.id] || 0;
                    const lastGroupMessage = groupLastMessageById[group.id];
                    const conversationKey = getConversationKey('group', group.id);
                    const preference = getConversationPreference(conversationKey);
                    const rowUpdating = updatingConversationId === conversationKey;

                    return (
                      <button
                        key={group.id}
                        className={cn(
                          "w-full flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3.5 rounded-xl transition-all duration-200 mb-1 sm:mb-1.5 group relative overflow-hidden",
                          isSelected
                              ? 'bg-gradient-to-r from-blue-500 to-cyan-600 shadow-lg scale-[1.02]'
                            : 'hover:bg-gray-50/80 dark:hover:bg-slate-800/70 hover:shadow-sm'
                        )}
                        onClick={() => handleGroupSelect(group.id)}
                      >
                        <div className="relative flex-shrink-0">
                          <div className={cn(
                            "w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shadow-md ring-2",
                            isSelected
                              ? 'bg-white/20 ring-white/30 text-white'
                              : 'bg-indigo-100 ring-indigo-200 text-indigo-600 dark:bg-indigo-900/40 dark:ring-indigo-800 dark:text-indigo-300'
                          )}>
                            <UsersRound className="w-5 h-5 sm:w-6 sm:h-6" />
                          </div>
                        </div>

                        <div className="flex-1 text-left min-w-0 relative z-10">
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <h3 className={cn(
                              "font-semibold text-sm sm:text-[15px] truncate",
                              isSelected ? 'text-white' : 'text-gray-900 dark:text-slate-100'
                            )}>
                              {group.name}
                            </h3>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void updateConversationPreference(conversationKey, { pinned: !preference.pinned });
                                }}
                                disabled={rowUpdating}
                                className="inline-flex items-center justify-center rounded-full p-1 hover:bg-black/5 dark:hover:bg-white/10"
                                aria-label={preference.pinned ? 'Unpin conversation' : 'Pin conversation'}
                              >
                                <Star className={cn(
                                  "w-3 h-3 sm:w-4 sm:h-4",
                                  preference.pinned
                                    ? 'text-yellow-400 fill-yellow-400'
                                    : isSelected
                                      ? 'text-yellow-200'
                                      : 'text-gray-300 group-hover:text-yellow-400'
                                )} />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void updateConversationPreference(conversationKey, { archived: !preference.archived });
                                }}
                                disabled={rowUpdating}
                                className="inline-flex items-center justify-center rounded-full p-1 hover:bg-black/5 dark:hover:bg-white/10"
                                aria-label={preference.archived ? 'Restore conversation' : 'Archive conversation'}
                              >
                                {preference.archived ? (
                                  <ArchiveRestore className={cn(
                                    "w-3 h-3 sm:w-4 sm:h-4",
                                    isSelected ? 'text-blue-100' : 'text-gray-400 dark:text-slate-400'
                                  )} />
                                ) : (
                                  <Archive className={cn(
                                    "w-3 h-3 sm:w-4 sm:h-4",
                                    isSelected ? 'text-blue-100' : 'text-gray-400 dark:text-slate-400'
                                  )} />
                                )}
                              </button>
                            </div>
                          </div>

                          <div className="flex items-center justify-between gap-2">
                            <p className={cn(
                              "text-xs sm:text-[13px] truncate font-medium",
                              isSelected ? 'text-indigo-100' : 'text-gray-500 dark:text-slate-400'
                            )}>
                              {getGroupPreview(group.id)}
                            </p>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {lastGroupMessage ? (
                                <span className={cn(
                                  "text-[11px]",
                                  isSelected ? 'text-indigo-100' : 'text-gray-400 dark:text-slate-500'
                                )}>
                                  {formatLastMessageTime(lastGroupMessage.createdAt)}
                                </span>
                              ) : null}
                              {unreadForGroup > 0 ? (
                                <span className="bg-red-500 text-white text-[10px] rounded-full min-w-[1.1rem] h-[1.1rem] px-1.5 inline-flex items-center justify-center font-semibold">
                                  {unreadForGroup > 9 ? '9+' : unreadForGroup}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* Main Chat Area */}
        <div className={cn(
          "flex-1 flex flex-col bg-white dark:bg-slate-950 overflow-hidden min-w-0",
          (!selectedUser && !selectedGroupId) ? "hidden sm:flex" : "flex"
        )}>
          {selectedGroup ? (
            <>
              <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-100 dark:border-slate-800 bg-gradient-to-r from-white to-gray-50/30 dark:from-slate-950 dark:to-slate-900/30 backdrop-blur-sm flex-shrink-0">
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="sm:hidden w-8 h-8 flex-shrink-0"
                      onClick={() => {
                        setSelectedGroupId(null);
                      }}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>

                    <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center ring-2 ring-indigo-200 dark:ring-indigo-800 shadow-md">
                      <UsersRound className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 dark:text-indigo-300" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-slate-100 truncate mb-0.5 sm:mb-1">
                        {selectedGroup.name}
                      </h2>
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 truncate">
                        {selectedGroup.participants.length} participants - {getGroupParticipantPreview(selectedGroup)}
                      </p>
                      {isEphemeralModeActive ? (
                        <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] sm:text-xs font-semibold text-amber-700">
                          <TimerReset className="h-3 w-3" />
                          EPHEMERAL ON
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openGroupAICopilot}
                      className="border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100/60 dark:hover:bg-indigo-900/40 text-xs px-2.5 h-8 gap-1.5 rounded-lg shadow-sm"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                      <span className="hidden sm:inline font-medium">AI Recap & Actions</span>
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:text-slate-200 transition-colors rounded-full w-10 h-10"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-hidden">
                <Chat
                  groupId={selectedGroup.id}
                  groupName={selectedGroup.name}
                  groupParticipants={selectedGroup.participants}
                  onEphemeralStateChange={setIsEphemeralModeActive}
                />
              </div>
            </>
          ) : selectedUser && selectedUserInfo ? (
            <>
              {/* Chat Header */}
              <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-100 dark:border-slate-800 bg-gradient-to-r from-white to-gray-50/30 dark:from-slate-950 dark:to-slate-900/30 backdrop-blur-sm flex-shrink-0">
                <div className="flex flex-col gap-2 sm:gap-4">
                  {/* Top row: User info with online status */}
                  <div className="flex items-center justify-between gap-2 sm:gap-4">
                    <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                      {/* Back button - mobile only */}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="sm:hidden w-8 h-8 flex-shrink-0"
                        onClick={() => {
                          setSelectedUser(null);
                          setSelectedUserInfo(null);
                          setSelectedGroupId(null);
                        }}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </Button>
                      
                      <div className="relative flex-shrink-0">
                        <img 
                          src={selectedUserInfo.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${selectedUserInfo.name}`} 
                          alt={selectedUserInfo.name}
                          className="w-10 h-10 sm:w-14 sm:h-14 rounded-full object-cover ring-2 ring-blue-100 dark:ring-slate-800 shadow-md"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-slate-100 truncate mb-0.5 sm:mb-1">
                          {selectedUserInfo.name}
                        </h2>
                        <div className="hidden sm:block">
                          <OnlineStatusIndicator 
                            userId={selectedUserInfo.id} 
                            showLabel={true} 
                            showLastSeen={true}
                          />
                        </div>
                        {isEphemeralModeActive ? (
                          <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] sm:text-xs font-semibold text-amber-700">
                            <TimerReset className="h-3 w-3" />
                            EPHEMERAL ON
                          </div>
                        ) : null}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={openDirectMessageAICopilot}
                        className="border-blue-200 dark:border-blue-800/60 bg-blue-50/50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100/60 dark:hover:bg-blue-900/40 text-xs px-2.5 h-8 gap-1.5 rounded-lg shadow-sm"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                        <span className="hidden sm:inline font-medium">AI Copilot</span>
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="hidden sm:flex hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:text-slate-200 transition-colors rounded-full w-10 h-10"
                      >
                        <Bell className="w-5 h-5" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:text-slate-200 transition-colors rounded-full w-10 h-10"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>

                  {/* Bottom row: Action buttons */}
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-sm hover:shadow-md transition-all duration-200"
                      onClick={handleAudioCall}
                      disabled={isOtherConversationActive}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      {t('messages.audioCall')}
                    </Button>
                    <Button 
                      size="sm" 
                      className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white shadow-sm hover:shadow-md transition-all duration-200"
                      onClick={handleVideoCall}
                      disabled={isOtherConversationActive}
                    >
                      <Video className="w-4 h-4 mr-2" />
                      {t('messages.videoCall')}
                    </Button>
                    <Button 
                      size="sm"
                      className={cn(
                        "flex-1 text-white shadow-sm hover:shadow-md transition-all duration-200",
                        isScreenSharing && isSelectedConversationInActiveCall
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                          : 'bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700'
                      )}
                      onClick={() => {
                        void handleScreenShare();
                      }}
                      disabled={isOtherConversationActive}
                    >
                      <MonitorUp className="w-4 h-4 mr-2" />
                      {isScreenSharing && isSelectedConversationInActiveCall ? 'Stop Share' : 'Share Screen'}
                    </Button>
                    <Button
                      size="sm" 
                      variant="outline"
                      className="flex-1 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-900 hover:border-gray-300 dark:text-slate-100 transition-all duration-200"
                      onClick={messageMediaUploader.openUploader}
                    >
                      <Paperclip className="w-4 h-4 mr-2" />
                      {t('messages.attach')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Chat Component */}
              <div className="flex-1 overflow-hidden">
                <Chat
                  receiverId={selectedUser}
                  onEphemeralStateChange={setIsEphemeralModeActive}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center relative bg-gradient-to-br from-gray-50 to-blue-50/30 dark:from-slate-950 dark:to-slate-900/30">
              <div className="text-center px-8 max-w-md">
                {/* Animated icon */}
                <div className="relative inline-block mb-6">
                  <div className="w-28 h-28 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform duration-300">
                    <MessageCircle className="w-14 h-14 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full animate-pulse shadow-lg"></div>
                </div>
                
                <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent mb-3">
                  {t('messages.selectConversation')}
                </h3>
                <p className="text-gray-600 dark:text-slate-300 mb-2 font-medium">{t('messages.chooseFriend')}</p>
                <p className="text-sm text-gray-500 dark:text-slate-400 mb-8">{t('messages.conversationsAppear')}</p>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/calls')}
                    className="w-full sm:w-auto border-2 border-gray-200 dark:border-slate-700 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-slate-900 dark:text-slate-100 transition-all duration-200 rounded-xl shadow-sm"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    {t('calls.callHistory')}
                  </Button>
                  <Button 
                    onClick={() => navigate('/friends')}
                    className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all duration-200 rounded-xl"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    {t('calls.findFriends')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Universal Media Uploader for Messages */}
        <UniversalMediaUploader
          isOpen={messageMediaUploader.isOpen}
          onClose={messageMediaUploader.closeUploader}
          onUpload={messageMediaUploader.handleUpload}
          onFilesChange={messageMediaUploader.handleFilesChange}
          config={messageMediaUploader.config}
          initialFiles={messageMediaUploader.files}
        />

        <Dialog open={isCreateGroupOpen} onOpenChange={setIsCreateGroupOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Group Chat</DialogTitle>
              <DialogDescription>
                Add at least two friends and give your group a name.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Group name"
              />

              <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-3">
                {friends.length === 0 ? (
                  <p className="text-sm text-gray-500">No friends available yet.</p>
                ) : (
                  friends.map((friend) => (
                    <label
                      key={friend.id}
                      className="flex items-center gap-3 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedGroupMemberIds.includes(friend.otherUser.id)}
                        onCheckedChange={() => toggleGroupMember(friend.otherUser.id)}
                      />
                      <img
                        src={friend.otherUser.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${friend.otherUser.name}`}
                        alt={friend.otherUser.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{friend.otherUser.name}</p>
                        {friend.otherUser.username ? (
                          <p className="text-xs text-gray-500 truncate">@{friend.otherUser.username}</p>
                        ) : null}
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateGroupOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => void handleCreateGroup()}
                disabled={creatingGroup || !groupName.trim() || selectedGroupMemberIds.length < 2}
              >
                {creatingGroup ? 'Creating...' : 'Create Group'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AIMessagesAdvisorDialog
          open={isAICopilotOpen}
          onOpenChange={setIsAICopilotOpen}
          activeChatTitle={aiChatTitle}
          initialSnippet={aiInitialSnippet}
        />
      </div>
    </div>
  );
}